import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { Calendar } from "primereact/calendar"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import EditorField from "../Utils/EditorField.js"
import styles from "../../../styles/Participant-Employment.module.css"
import { UserContext } from "../../../context/userContext"
import TransitionPlan from "../TransitionPlan"
import {
  Actions,
  showError,
  convertToLocalDateTime,
  formatDateAndDuration,
  requieredFieldIsMissing,
  renderRequireFieldErrorMessage,
  addOneMinute,
  substractOneMinute,
  formatDate,
} from "../Utils/Utils"
import { YesNoUnknown, EngagementChannels } from "../Types/Dropdowns"
import RadioButtonField from "../Utils/RadioButtonField"
import { employmentApiUrl } from "../../../config/microservicesUrls"

const api_url = employmentApiUrl()

export default function EmploymentReferral({
  action,
  closeForm,
  referral,
  updateData,
  viewMode,
  transitionPlan,
  setTransitionPlan,
}) {
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const [loading, setLoading] = useState(false)
  const [formErrors, setFormErrors] = useState(null)

  const defaultData = {
    engagementDate: formatDate(new Date()),
    engagementChannel: EngagementChannels[0],
    startTime: new Date(),
    endTime: addOneMinute(new Date()),
    vocationalRehabCounselor: "",
    refSentToVocRehab: YesNoUnknown[2],
    otherHealthServiceProviders: "",
    workComments: "",
    interestInMoreEducation: "",
    infoAboutPersonsIllness: "",
    personsStrengths: "",
    jobMatchRecommendation: "",
  }

  const [data, setData] = useState(defaultData)

  useEffect(() => {
    if (referral) {
      setData({
        id: referral.id,
        engagementDate: convertToLocalDateTime(referral.engagementDate),
        engagementChannel: EngagementChannels[referral.engagementChannel],
        startTime: convertToLocalDateTime(referral.startTime),
        endTime: convertToLocalDateTime(referral.endTime),
        vocationalRehabCounselor: referral.vocationalRehabCounselor,
        refSentToVocRehab: YesNoUnknown[referral.refSentToVocRehab],
        otherHealthServiceProviders: referral.otherHealthServiceProviders,
        workComments: referral.workComments,
        interestInMoreEducation: referral.interestInMoreEducation,
        infoAboutPersonsIllness: referral.infoAboutPersonsIllness,
        personsStrengths: referral.personsStrengths,
        jobMatchRecommendation: referral.jobMatchRecommendation,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearForm = () => {
    setData(defaultData)
    setFormErrors(null)
  }

  const handleSave = async () => {
    const { formattedDate, formattedStartTime, formattedEndTime } =
      formatDateAndDuration(data.engagementDate, data.startTime, data.endTime)
    const engagementChannel = EngagementChannels.indexOf(data.engagementChannel)
    const refSentToVocRehab = YesNoUnknown.indexOf(data.refSentToVocRehab)
    setLoading(true)
    if (action === Actions.Add) {
      axios
        .post(
          `${api_url}/referral`,
          {
            createdBy: userCtx.session.user.staff_id,
            ...data,
            engagementChannel,
            engagementDate: formattedDate,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            refSentToVocRehab,
            organizationId: userCtx.session.user.organization_id,
            participantId: userCtx.current_participant_id,
          },
          { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
        )
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to create referral", errors)
        })
    } else {
      axios
        .put(
          `${api_url}/referral`,
          {
            updatedBy: userCtx.session.user.staff_id,
            ...data,
            engagementChannel,
            engagementDate: formattedDate,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            refSentToVocRehab,
            organizationId: userCtx.session.user.organization_id,
            participantId: userCtx.current_participant_id,
          },
          { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
        )
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to update referral", errors)
        })
    }
  }

  const renderFormErrors = () => {
    if (formErrors !== null) {
      return (
        <ul className={styles.errors_list}>
          {formErrors?.map((error, index) => {
            return (
              <li key={index}>
                <small className="p-error">{error}</small>
              </li>
            )
          })}
        </ul>
      )
    }
    return null
  }
  return (
    <>
      <Toast ref={toast} position="top-right" />
      {transitionPlan !== null ? (
        <TransitionPlan
          action={action}
          transitionPlan={transitionPlan}
          closeForm={() => {
            setTransitionPlan(null)
          }}
          updateData={updateData}
          viewMode={viewMode}
        />
      ) : (
        <div className="main-content">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h1 className={styles.view_title}>
              {action === Actions.Add
                ? "Add employment referral"
                : action === Actions.Edit
                  ? "Edit employment referral"
                  : "Employment referral"}
            </h1>
            <Button
              className={styles.rounded_btn}
              icon="pi pi-times"
              type="submit"
              disabled={loading}
              onClick={() => {
                clearForm()
                closeForm()
              }}
            />
          </div>
          <div className={"card flex flex-wrap p-fluid " + styles.wrapper}>
            <h3 className={styles.view_subtitle}>Employment referral</h3>
            <hr className={styles.line}></hr>
            <div className={styles.flex_card}>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  Engagement date
                </label>
                <Calendar
                  name="Engagement date"
                  value={data.engagementDate}
                  panelClassName="custom_calendar_panel"
                  onChange={(e) =>
                    setData({ ...data, engagementDate: e.value })
                  }
                  dateFormat="mm/dd/yy"
                  showIcon
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.engagementDate)
                      ? styles.invalid
                      : ""
                    }`}
                  disabled={action === Actions.View || viewMode?.view}
                />
                {renderRequireFieldErrorMessage(
                  data.engagementDate,
                  "Engagement date is required"
                )}
              </div>
              <div className={styles.field} style={{ flexGrow: "1" }}>
                <label className={`${styles.label} ${styles.required}`}>
                  Engagement channel
                </label>
                <Dropdown
                  value={data.engagementChannel}
                  options={EngagementChannels}
                  panelClassName="custom_dropdown_panel"
                  onChange={(e) =>
                    setData({ ...data, engagementChannel: e.value })
                  }
                  placeholder="Select a channel"
                  style={{ height: "32px", alignItems: "center" }}
                  disabled={action === Actions.View || viewMode?.view}
                />
              </div>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  Start time
                </label>
                <Calendar
                  name="Start time"
                  value={data.startTime}
                  timeOnly
                  panelClassName="custom_calendar_panel"
                  onChange={(e) => setData({ ...data, startTime: e.value })}
                  hourFormat="12"
                  showIcon
                  icon="pi pi-clock"
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.startTime)
                      ? styles.invalid
                      : ""
                    }`}
                  maxDate={substractOneMinute(data.endTime)}
                  disabled={action === Actions.View || viewMode?.view}
                />
                {renderRequireFieldErrorMessage(
                  data.startTime,
                  "Start time is required"
                )}
              </div>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  End time
                </label>
                <Calendar
                  name="End time"
                  value={data.endTime}
                  timeOnly
                  panelClassName="custom_calendar_panel"
                  hourFormat="12"
                  onChange={(e) => setData({ ...data, endTime: e.value })}
                  showIcon
                  icon="pi pi-clock"
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.endTime) ? styles.invalid : ""
                    }`}
                  minDate={addOneMinute(data.startTime)}
                  disabled={action === Actions.View || viewMode?.view}
                />
                {renderRequireFieldErrorMessage(
                  data.endTime,
                  "End time is required"
                )}
              </div>
            </div>
            <div className={styles.flex_card}>
              <div className={styles.field} style={{ flexGrow: "1" }}>
                <label className={styles.label}>
                  State vocational rehabilitation counselor
                </label>
                <InputText
                  className="p-inputtext-md"
                  value={data.vocationalRehabCounselor}
                  onChange={(e) =>
                    setData({
                      ...data,
                      vocationalRehabCounselor: e.target.value,
                    })
                  }
                  disabled={action === Actions.View || viewMode?.view}
                  name="vocationalRehabCounselor"
                />
              </div>
              <div className={styles.field}>
                <RadioButtonField
                  label="Was referral sent to state vocational rehabilitation?"
                  value={data.refSentToVocRehab}
                  setValue={(e) => setData({ ...data, refSentToVocRehab: e })}
                  disabled={action === Actions.View || viewMode?.view}
                />
              </div>
            </div>
            <EditorField
              value={data.otherHealthServiceProviders}
              onTextChange={(e) =>
                setData({ ...data, otherHealthServiceProviders: e })
              }
              label="Other healthcare/social service providers"
              disabled={action === Actions.View || viewMode?.view}
            />

            <h3 className={styles.view_subtitle}>Additional information</h3>
            <hr className={styles.line}></hr>

            <EditorField
              value={data.workComments}
              onTextChange={(e) => setData({ ...data, workComments: e })}
              label="What is the person saying about work?"
              placeholder="What do they want to work now? What type of job?"
              disabled={action === Actions.View || viewMode?.view}
            />

            <EditorField
              value={data.interestInMoreEducation}
              onTextChange={(e) =>
                setData({ ...data, interestInMoreEducation: e })
              }
              label="Is the person interested in gaining more education now to advance their career goals?"
              disabled={action === Actions.View || viewMode?.view}
            />

            <EditorField
              value={data.infoAboutPersonsIllness}
              onTextChange={(e) =>
                setData({ ...data, infoAboutPersonsIllness: e })
              }
              label="Please include information about the person's illness (diagnosis if reported, symptoms, etc.)"
              placeholder="How might the person's illness (and/or substance use) affect a job or return to school"
              disabled={action === Actions.View || viewMode?.view}
            />

            <EditorField
              value={data.personsStrengths}
              onTextChange={(e) => setData({ ...data, personsStrengths: e })}
              label="What are some of the person's strengths?"
              placeholder="Experience, training, personality, supports, etc..."
              disabled={action === Actions.View || viewMode?.view}
            />

            <EditorField
              value={data.jobMatchRecommendation}
              onTextChange={(e) =>
                setData({ ...data, jobMatchRecommendation: e })
              }
              label="What job do you and the individual think would be a good match?"
              placeholder="Type of job, hours, etc..."
              disabled={action === Actions.View || viewMode?.view}
            />
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "flex-end",
              }}
            >
              <Button
                className={styles.cancel_button}
                type="submit"
                disabled={loading}
                onClick={() => {
                  clearForm()
                  closeForm()
                }}
              >
                {action === Actions.View ? "Back" : "Cancel"}
              </Button>
              {action !== Actions.View && !viewMode && (
                <Button
                  className={styles.createReferralBtn}
                  type="submit"
                  disabled={
                    requieredFieldIsMissing(data.engagementDate) ||
                    requieredFieldIsMissing(data.startTime) ||
                    requieredFieldIsMissing(data.endTime) ||
                    requieredFieldIsMissing(data.engagementChannel) ||
                    loading
                  }
                  icon={loading ? "pi pi-spin pi-spinner" : ""}
                  iconPos="right"
                  label={`${action === Actions.Add ? "Create" : "Update"
                    } employment referral`}
                  onClick={() => handleSave()}
                />
              )}
            </div>
            {renderFormErrors()}
          </div>
        </div>
      )}
    </>
  )
}
